// Debrief screen. Twin of the desktop debrief renderer: fetch the form HTML,
// render it (sanitized), and offer a single acknowledge action that advances
// (typically into natural completion). An empty form still shows the
// acknowledge button so the participant can finish.

import { useEffect, useState } from "react";
import type { WebRendererProps } from "./registry";
import { fetchForm } from "../session/sessionDriver";
import { Card, Button, Loading } from "../ui/primitives";
import {
  FormBlocksView,
  ParticipantGate,
  gateRecording,
  gateSatisfied,
  interactiveBlocks,
  parseFormContent,
  parseGate,
  type GateValues,
} from "./formBlocks";

export default function DebriefRenderer({ session, onDone }: WebRendererProps) {
  const [content, setContent] = useState<string | null>(null);
  const [gateValues, setGateValues] = useState<GateValues>({});

  useEffect(() => {
    let alive = true;
    // On a load failure, fall back to empty content: the participant must still
    // be able to finish (matches the desktop's empty-content behavior).
    (async () => {
      try {
        const r = await fetchForm(session);
        if (alive) setContent(r.content);
      } catch {
        if (alive) setContent("");
      }
    })();
    return () => {
      alive = false;
    };
  }, [session]);

  if (content === null) return <Loading message="Loading..." />;

  // Both buttons complete the debrief. "Decline data use" is record-only and
  // does NOT abort - the participant still finishes (recording is Phase 2, so
  // for now it submits the same acknowledge result as I Understand).
  const gate = parseGate(content);
  const blocks = parseFormContent(content);
  const satisfied = gateSatisfied(blocks, gateValues);
  // Both buttons finish the debrief. Decline is record-only (data_use_declined);
  // the interactive record rides along only when the form has interactive blocks.
  const finish = (declinedDataUse: boolean) => {
    const rec = interactiveBlocks(blocks).length
      ? gateRecording(blocks, gateValues)
      : {};
    onDone({
      kind: "submit",
      result: declinedDataUse
        ? { acknowledged: true, data_use_declined: true, ...rec }
        : { acknowledged: true, ...rec },
    });
  };
  return (
    <Card>
      {content ? (
        <FormBlocksView blocks={blocks} />
      ) : (
        <p className="jol-body">Thank you for taking part.</p>
      )}
      <ParticipantGate
        blocks={blocks}
        values={gateValues}
        onChange={(id, value) => setGateValues((v) => ({ ...v, [id]: value }))}
      />
      <div className="jol-row">
        <Button
          variant="secondary"
          style={gate?.decline_color ? { backgroundColor: gate.decline_color, color: "white" } : undefined}
          onClick={() => finish(true)}
        >
          {gate?.decline_label ?? "Decline data use"}
        </Button>
        <Button
          variant="primary"
          disabled={!satisfied}
          style={gate?.agree_color ? { backgroundColor: gate.agree_color, color: "white" } : undefined}
          onClick={() => finish(false)}
        >
          {gate?.agree_label ?? "Continue"}
        </Button>
      </div>
    </Card>
  );
}
